import React, { useState } from 'react';
import { Box, Divider } from '@mui/material';

import { Typography } from '@occc-app/design-system';
import { Activity_Type } from '@occc/graphql';
import { useGetActivityLog } from '@occc-app/dataAccessLayer/site/queries';
import config from '@occc/config';

import { Tabs } from './location-activity-log-tabs.component';
import LocationActivityList from './location-activity-list.component';

import { LocationActivityLogProps } from '../types';

type Tab = {
  value: Activity_Type;
  label: string;
  content: React.ReactNode;
};

export const LocationActivityLog = ({
  location,
  onRequestDetailsView,
}: LocationActivityLogProps) => {
  const {
    identifier,
    mobileEnabled = false,
    loyaltyEnabled = false,
  } = location.siteSystemConfiguration?.[0] || {};
  const [activeTabValue, setActiveTabValue] = useState<Activity_Type>(
    mobileEnabled ? Activity_Type.Mobile : Activity_Type.Loyalty,
  );
  const { data, loading, paginationProps } = useGetActivityLog(
    {
      locationId: identifier,
      activityType: activeTabValue,
    },
    8,
    {
      pollInterval: config.activityLogPollingInterval,
    },
  );

  const tabs: Tab[] = [];

  if (mobileEnabled) {
    tabs.push({
      value: Activity_Type.Mobile,
      label: 'Mobile',
      content: (
        <LocationActivityList
          data={data}
          onRequestDetailsView={onRequestDetailsView}
          paginationProps={paginationProps}
          timezone={location.timezone}
          loading={loading}
        />
      ),
    });
  }

  if (loyaltyEnabled) {
    tabs.push({
      value: Activity_Type.Loyalty,
      label: 'Loyalty',
      content: (
        <LocationActivityList
          data={data}
          onRequestDetailsView={onRequestDetailsView}
          paginationProps={paginationProps}
          timezone={location.timezone}
          loading={loading}
        />
      ),
    });
  }

  if (tabs.length < 1) {
    return (
      <Typography color='primary' px={2} pt={1}>
        Activity log is disabled. Enable mobile or loyalty to access activity log.
      </Typography>
    );
  }

  return (
    <Tabs tabs={tabs} activeTabValue={activeTabValue} onChange={setActiveTabValue}>
      {(tabsComponent: React.ReactNode, content: React.ReactNode) => (
        <>
          <Box display='flex' alignItems='center' justifyContent='space-between' px={2}>
            {tabsComponent}
          </Box>
          <Divider />
          {content}
        </>
      )}
    </Tabs>
  );
};
